import { useId, type ReactNode } from "react";
import clsx from "clsx";
import styles from "./Card.module.scss";

export interface CardProps {
  /** Base id for the heading; the card wires `aria-labelledby` to `{id}-heading`. */
  id?: string;
  /** Omit for a headerless shell — the loading/error variants of a card that has no title yet. */
  title?: ReactNode;
  /**
   * How the title is DRESSED — the tag stays `<h3>` either way, so the outline and the card's
   * `aria-labelledby` name are unaffected.
   *
   * - `display` (default) — Archivo Black 16px sentence case. Every rebuilt page uses this.
   * - `label` — the mono-caps lockup: 11px/700, tracked, uppercase, ink. STYLE-GUIDE ruling 7 says
   *   the treatment decides, not the tag ("an `<h3>` that is visually an eyebrow IS an eyebrow"),
   *   and ruling 5 puts eyebrow labels in mono. The one divergence from ruling 5 is the colour:
   *   the Advance Docs specimen's `.vhead__label` is `--ac-color-ink`, not `t3`, because in that
   *   module the label IS the card's heading rather than a kicker above one.
   *
   * Opt-in on purpose: the default is unchanged so no other page moves.
   */
  titleVariant?: "display" | "label";
  /** The card's persistent purpose line, under the title. */
  subtitle?: ReactNode;
  /**
   * How TIGHT the sub-line sits.
   *
   * - `default` — `--ac-fs-md` (13px), `--ac-space-2` (8px) under the title.
   * - `tight` — `--ac-fs-sm` (11px), `--ac-space-1` (4px) under it. Asked for on Your Crew
   *   ("subtext can be smaller here and closer under 'your crew'", TASK-004).
   *
   * Opt-in for the same reason `titleVariant` is: 53 call sites pass a `subtitle`, and there is no
   * specimen in the repo to measure a system-wide change against — the contacts Dev Reference was
   * only ever decoded to text, so its CSS is not recoverable here. If the density is wanted on every
   * card, flip the DEFAULT rather than adding `tight` at 53 call sites.
   */
  subtitleVariant?: "default" | "tight";
  /** Header actions, right-aligned (buttons, a kebab). */
  actions?: ReactNode;
  /**
   * Float the actions out of the header row so a button taller than the title doesn't drive the
   * header's height.
   *
   * A header {@link actions} button (~36px) is taller than a single-line title (~19px), so a
   * title-only card with an action is stretched to the button's height, hanging a wedge of dead space
   * under the title before the body. With this set the actions are positioned top-right, centred on
   * the title line, and the header hugs the title — so a title-only card with an action reads with the
   * same header→body gap as one without (Contacts' Friends card vs its Top 8).
   *
   * Opt-in on purpose (the `subtitleVariant` reasoning): a STACKED header — one with a {@link subtitle}
   * — already has a two-line title block that matches the button height, so it needs none of this, and
   * a floated action reserves no width, so a card whose title can grow long next to its action should
   * stay inline. Only reach for it on a title-only card with a single, compact action.
   */
  floatActions?: boolean;
  /**
   * Edge-to-edge body: the card drops its own padding so a row list can run the full width, and the
   * header keeps an inset of its own. Contacts' cards are flush; Assets' are padded.
   */
  flush?: boolean;
  /**
   * How much CHROME the shell carries. Orthogonal to {@link flush}, which is about padding.
   *
   * - `solid` (default) — the shared shell: paper, 1px line, radius, card shadow.
   * - `transparent` — a card's GROUPING and rhythm with none of its chrome: no background, no border,
   *   no shadow, no padding. For a cluster that should read as one object ON the page rather than as
   *   a panel sitting on it. Pair with no `title` for the fully bare form; the header slot still works
   *   if a group wants a label without a panel around it.
   *
   * `transparent` ignores `flush` — there is no padding left for it to remove.
   */
  variant?: "solid" | "transparent";
  className?: string;
  children?: ReactNode;
}

/**
 * The one card shell. Both design specs describe the same object — `.card` (Contacts) and `.acard`
 * (Assets) are byte-for-byte the same shell, `paper / 1px line / --r-2xl / --sh-card`, with Assets
 * adding padding. So this is one component with a `flush` variant rather than two card languages.
 *
 * Header anatomy is the specs' too: an `h3` in the display face (a card title is an h3 — page h1 →
 * section h2 → card h3), an optional sub-line, and right-aligned actions.
 */
export function Card({ id, title, titleVariant = "display", subtitle, subtitleVariant = "default", actions, floatActions, flush, variant = "solid", className, children }: CardProps) {
  const autoId = useId();
  const labelId = `${id ?? autoId}-heading`;
  const transparent = variant === "transparent";
  return (
    <section
      className={clsx(
        styles.card,
        // `transparent` supplies its own layout and cancels the chrome, so it replaces the
        // padded/flush pair rather than stacking on one of them.
        transparent ? styles.transparent : flush ? styles.flush : styles.padded,
        className,
      )}
      aria-labelledby={title ? labelId : undefined}
      data-title-variant={titleVariant === "label" ? "label" : undefined}
    >
      {title && (
        <header
          className={styles.head}
          data-stacked={subtitle ? "" : undefined}
          // Float only makes sense for a title-only header with an action — a stacked (subtitled) block
          // already matches the button height, so it stays inline.
          data-float-actions={floatActions && actions && !subtitle ? "" : undefined}
        >
          <div className={styles.headMain}>
            <h3 id={labelId} className={clsx(styles.title, titleVariant === "label" && styles.titleLabel)}>
              {title}
            </h3>
            {subtitle && (
              <p className={styles.subtitle} data-variant={subtitleVariant === "tight" ? "tight" : undefined}>
                {subtitle}
              </p>
            )}
          </div>
          {actions && <div className={styles.actions}>{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

/**
 * The card empty treatment: a recessed well holding ONE sentence, naming its action in bold rather
 * than repeating it as a second button — the header already carries that button. Deliberately not
 * `<EmptyState>` (title + description + CTA), which stacks badly when a screen shows four cards.
 */
export function CardEmpty({ children }: { children: ReactNode }) {
  return <p className={styles.empty}>{children}</p>;
}

/** The action name inside that sentence ("click **Add crew** to start your team"). */
export function CardEmptyAction({ children }: { children: ReactNode }) {
  return <strong className={styles.emptyAction}>{children}</strong>;
}
